import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import * as LucideIcons from 'lucide-react';

/**
 * ============================================================================
 * NavigationCardsBlock - Cartes de Navigation CMS
 * ============================================================================
 * 
 * VERSION: 2.0 - FULL CMS SUPPORT
 * 
 * DESIGN SYSTEM CONTRACT:
 * ✅ width: 100% du parent (le layout contrôle la largeur)
 * ✅ padding interne configurable
 * ✅ styles internes (background, typography)
 * ❌ INTERDIT: max-width basé sur viewport
 * ❌ INTERDIT: width en % du viewport
 * ❌ INTERDIT: margin auto pour contrôler la largeur globale
 * 
 * RÈGLE: Le LAYOUT parent définit la largeur, pas le bloc.
 * 
 * Structure content attendue (CMS):
 * {
 *   title: "Explorer les Nouveautés",
 *   cards: [
 *     {
 *       id: "uuid",
 *       title: "Nouveautés en cours",
 *       description: "Découvrez les nouveautés actuellement disponibles",
 *       link: "/dashboard/nouveautes/en-cours",
 *       image: "optional URL",
 *       icon: "Clock" (Lucide icon name),
 *       color: "bg-green-500/10 text-green-600 border-green-500/20",
 *       cta_label: "Découvrir",
 *       is_visible: true,
 *       order: 0
 *     }
 *   ]
 * }
 * 
 * FALLBACK: Si aucune carte CMS visible, retourne null (pageConfig.js prend le relais dans HubPage)
 */

// Icônes par défaut pour le fallback
const DEFAULT_ICONS = {
  sparkles: LucideIcons.Sparkles,
  calendar: LucideIcons.Calendar,
  clock: LucideIcons.Clock,
  tag: LucideIcons.Tag,
  star: LucideIcons.Star,
  gift: LucideIcons.Gift,
  package: LucideIcons.Package,
  zap: LucideIcons.Zap,
  heart: LucideIcons.Heart,
  'shopping-cart': LucideIcons.ShoppingCart,
  users: LucideIcons.Users,
  store: LucideIcons.Store,
  gamepad: LucideIcons.Gamepad2,
  dice: LucideIcons.Dice5,
};

// Récupérer l'icône dynamiquement
// Compatible avec: null, undefined, "", "none" (legacy) → fallback Sparkles
const getIcon = (iconName) => {
  // Gestion valeurs vides/legacy
  if (!iconName || iconName === 'none' || iconName === '') {
    return LucideIcons.Sparkles;
  }
  
  // Essayer d'abord les icônes par défaut (lowercase)
  const lowerName = iconName.toLowerCase();
  if (DEFAULT_ICONS[lowerName]) {
    return DEFAULT_ICONS[lowerName];
  }
  
  // Essayer Lucide directement (PascalCase)
  if (LucideIcons[iconName]) {
    return LucideIcons[iconName];
  }
  
  // Fallback
  return LucideIcons.Sparkles;
};

/**
 * Normalise une couleur pour l'affichage
 * Compatible avec: null, undefined, "", "none" (legacy) → ""
 */
const normalizeColor = (color) => {
  if (!color || color === 'none' || color === '') {
    return '';
  }
  return color;
};

const NavigationCardsBlock = ({ block }) => {
  const { title, cards = [] } = block?.content || {};

  // Filtrer les cartes visibles
  const visibleCards = cards.filter(card => card.is_visible !== false);

  // Si aucune carte visible, ne rien afficher (fallback vers pageConfig.js)
  if (!visibleCards || visibleCards.length === 0) {
    return null;
  }

  // Trier par order
  const sortedCards = [...visibleCards].sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div 
      className="navigation-cards-block"
      data-testid="navigation-cards-block"
      data-block-type="navigation_cards"
      style={{
        // ============================================================
        // DESIGN SYSTEM CONTRACT: width = 100% du parent
        // Le layout parent contrôle la largeur, pas ce composant
        // ============================================================
        width: '100%',
        boxSizing: 'border-box'
      }}
    >
      {title && (
        <h2 className="text-2xl font-heading font-semibold mb-8 text-center">
          {title}
        </h2>
      )}
      
      <div className={`grid gap-6 ${
        sortedCards.length === 1 ? 'md:grid-cols-1 max-w-md mx-auto' :
        sortedCards.length === 2 ? 'md:grid-cols-2' : 
        sortedCards.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' :
        'md:grid-cols-3'
      }`}>
        {sortedCards.map((card, index) => {
          const IconComponent = getIcon(card.icon);
          const hasImage = card.image && card.image.trim() !== '';
          const cardColor = normalizeColor(card.color);
          
          // Construire l'URL de l'image
          const imageUrl = hasImage 
            ? (card.image.startsWith('/api') 
                ? `${process.env.REACT_APP_BACKEND_URL}${card.image}` 
                : card.image)
            : null;

          return (
            <Link 
              key={card.id || index} 
              to={card.link || '#'}
              className="block group"
              data-testid={`nav-card-${index}`}
            >
              <Card 
                className={`
                  h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1 
                  border-2 border-transparent hover:border-primary/20 overflow-hidden
                  ${cardColor}
                `}
              >
                {/* Image en haut si présente */}
                {imageUrl && (
                  <div className="relative h-32 overflow-hidden">
                    <img 
                      src={imageUrl} 
                      alt={card.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                  </div>
                )}

                <CardContent className={`p-6 flex flex-col items-center text-center ${hasImage ? 'pt-4' : ''}`}>
                  {/* Icône (si pas d'image ou en plus de l'image) */}
                  {!hasImage && (
                    <div 
                      className={`
                        w-16 h-16 rounded-full flex items-center justify-center mb-4 
                        group-hover:scale-110 transition-all duration-300
                        ${cardColor ? '' : 'bg-primary/10 group-hover:bg-primary/20'}
                      `}
                    >
                      <IconComponent className={`w-8 h-8 ${cardColor ? '' : 'text-primary'}`} />
                    </div>
                  )}
                  
                  <h3 className="text-lg font-semibold mb-2 group-hover:text-primary transition-colors">
                    {card.title}
                  </h3>
                  
                  {card.subtitle && (
                    <p className="text-sm text-muted-foreground mb-1">
                      {card.subtitle}
                    </p>
                  )}
                  
                  {card.description && (
                    <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
                      {card.description}
                    </p>
                  )}
                  
                  <Button 
                    variant="ghost" 
                    className="mt-auto group-hover:bg-primary/10"
                  >
                    {card.cta_label || 'Découvrir'}
                    <LucideIcons.ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default NavigationCardsBlock;
